import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Container from '../components/Container';
import SectionTitle from '../components/SectionTitle';
import Button from '../components/Button';
import Icon from '../components/Icon';

// Import content
import packageContent from '../content/service-packages.json';

/**
 * Service Packages page component
 * @returns {React.ReactElement} Service Packages page
 */
const ServicePackagesPage = () => {
  const { categories } = packageContent;
  const [activeTab, setActiveTab] = React.useState('construction');

  return (
    <div className="min-h-screen flex flex-col bg-brand-black text-white">
      <Header />
      
      {/* Empty space for header padding */}
      <div className="pt-32 md:pt-36 lg:pt-40"></div>

      {/* Tab Navigation */}
      <div className="bg-brand-black border-b border-brand-blue-600/30">
        <Container>
          <div className="flex justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`px-6 py-4 text-lg font-medium border-b-2 transition-colors ${activeTab === category.id ? 'text-brand-blue-600 border-brand-blue-600' : 'text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-400'}`}
                onClick={() => setActiveTab(category.id)}
              >
                {category.title}
              </button>
            ))}
          </div>
        </Container>
      </div>

      {/* Service Package Categories */}
      {categories.filter(category => category.id === activeTab).map((category) => (
        <section 
          key={category.id}
          id={category.id}
          className="py-8 md:py-12 bg-brand-black"
        >
          <Container>
            <SectionTitle 
              title={category.title}
              subtitle={category.description}
              className="mb-6"
            />
            
            {/* Bundle Pricing Info - Only for Real Estate category - Hidden on mobile */}
            {category.id === 'realestate' && (
              <div className="hidden md:block bg-brand-blue-900/30 border border-brand-blue-600/30 rounded-lg p-4 mb-8 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-brand-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold text-lg text-white">Volume Discount!</span>
                </div>
                <p className="text-white">5+ listings per month: save 10% on any package</p>
              </div>
            )}
            
            {/* Bundle Pricing Info - Only for Construction category - Hidden on mobile */}
            {category.id === 'construction' && (
              <div className="hidden md:block bg-brand-blue-900/30 border border-brand-blue-600/30 rounded-lg p-4 mb-8 text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-brand-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-bold text-lg text-white">Bundle & Save!</span>
                </div>
                <p className="text-white">Save 10% when you pre-pay for 3 months of any construction package. Contact our team to learn more.</p>
              </div>
            )}
            
            {/* Package Comparison Table - Hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full border-collapse mb-8">
                <thead>
                  <tr className="bg-brand-blue-900 text-white">
                    <th className="p-4 text-left">Service Tier</th>
                    <th className="p-4 text-left">Billing Frequency</th>
                    <th className="p-4 text-left">Key Deliverables</th>
                    <th className="p-4 text-left">.</th>
                    <th className="p-4 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {category.packages.map((pkg, index) => (
                    <tr 
                      key={pkg.id} 
                      className={`${index % 2 === 0 ? 'bg-gray-900' : 'bg-gray-800'} ${pkg.recommended ? 'border-l-4 border-brand-blue-600' : ''}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center">
                          <div className="bg-brand-blue-600/10 p-2 rounded-full mr-3">
                            <Icon name={pkg.icon} className="text-brand-blue-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{pkg.title}</h3>
                            {pkg.recommended && (
                              <span className="text-xs bg-brand-blue-600 text-white px-2 py-1 rounded-full mt-1 inline-block">Recommended</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{pkg.frequency}</td>
                      <td className="p-4">
                        <ul className="list-disc pl-5 space-y-1">
                          {pkg.deliverables.map((item, i) => (
                            <li key={i} className="text-sm">{item}</li>
                          ))}
                        </ul>
                      </td>
                      <td className="p-4 font-medium">{pkg.price}</td>
                      <td className="p-4">
                        <Button 
                          href={`/contact?package=${pkg.id}${category.id === 'construction' ? '&discount=true' : ''}${category.id === 'realestate' ? '&volume=true' : ''}`}
                          size="small"
                        >
                          Contact us for a free estimate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Package Cards for Mobile */}
            <div className="grid gap-8 md:hidden">
              {/* Bundle Pricing Info for Mobile - Only for Construction category */}
              {category.id === 'construction' && (
                <div className="bg-brand-blue-900/30 border border-brand-blue-600/30 rounded-lg p-4 mb-4 text-center">
                  <div className="flex items-center justify-center mb-2">
                    <svg className="w-5 h-5 text-brand-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm4.707 3.707a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L8.414 9H10a3 3 0 013 3v1a1 1 0 102 0v-1a5 5 0 00-5-5H8.414l1.293-1.293z" clipRule="evenodd" />
                    </svg>
                    <span className="font-bold text-lg text-white">Bundle & Save!</span>
                  </div>
                  <p className="text-white text-sm">Save 10% when you pre-pay for 3 months of any construction package. Contact our team to learn more.</p>
                </div>
              )}
              {category.packages.map((pkg) => (
                <div 
                  key={pkg.id} 
                  className={`bg-gray-900 rounded-lg p-6 ${pkg.recommended ? 'border-l-4 border-brand-blue-600' : ''}`}
                >
                  <div className="flex items-center mb-4">
                    <div className="bg-brand-blue-600/10 p-3 rounded-full mr-4">
                      <Icon name={pkg.icon} className="text-brand-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-white">{pkg.title}</h3>
                      {pkg.recommended && (
                        <span className="text-xs bg-brand-blue-600 text-white px-2 py-1 rounded-full mt-1 inline-block">Recommended</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm text-gray-400 mb-1">Billing Frequency</h4>
                    <p>{pkg.frequency}</p>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm text-gray-400 mb-1">Key Deliverables</h4>
                    <ul className="list-disc pl-5 space-y-1">
                      {pkg.deliverables.map((item, i) => (
                        <li key={i} className="text-sm">{item}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-sm text-gray-400 mb-1">.</h4>
                    <p className="font-medium">{pkg.price}</p>
                  </div>
                  
                  <Button 
                    href={`/contact?package=${pkg.id}${category.id === 'construction' ? '&discount=true' : ''}${category.id === 'realestate' ? '&volume=true' : ''}`}
                    fullWidth
                  >
                    Contact us for a free estimate
                  </Button>
                </div>
              ))}
            </div>

            {/* Upsell Options - Only for Construction category */}
            {category.id === 'construction-tbd' && (
              <div className="mt-12 bg-brand-blue-900/20 border border-brand-blue-600/30 rounded-xl p-6 md:p-8">
                <h3 className="text-2xl font-bold text-center mb-6">Optional Add-On Services</h3>
                <p className="text-center text-gray-300 mb-4">Enhance your construction monitoring with these specialized services</p>
                <p className="text-center text-brand-blue-600 font-medium mb-8 flex items-center justify-center">
                  <span className="text-xl mr-2">💡</span>
                  Bundle two or more add-ons and save 10%
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* RTK Accuracy Upgrade */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🛰️</span>
                      <h4 className="text-lg font-bold">RTK Accuracy Upgrade</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Achieve survey-grade accuracy (within 1–2 cm) using RTK positioning for orthomosaic maps and 3D models. Ideal for precise volume calculations and CAD alignment.</p>
                  </div>

                  {/* Ground Control Point Setup */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">📍</span>
                      <h4 className="text-lg font-bold">Ground Control Point Setup</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">We deploy and record permanent ground control markers to ensure every flight aligns perfectly with your site coordinates. A one-time setup that delivers long-term consistency.</p>
                  </div>

                  {/* Licensed Surveyor Validation */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🧭</span>
                      <h4 className="text-lg font-bold">Licensed Surveyor Validation</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">For clients requiring certified data, a licensed Texas surveyor reviews and signs off on your RTK deliverables—providing legal confidence in every measurement.</p>
                  </div>

                  {/* Timelapse Camera Monitoring */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">📸</span>
                      <h4 className="text-lg font-bold">Timelapse Camera Monitoring</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Add a solar-powered site camera for continuous progress visibility between drone visits. Includes secure cloud access and optional monthly timelapse video compilation.</p>
                  </div>

                  {/* Marketing Reel Editing */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🎬</span>
                      <h4 className="text-lg font-bold">Marketing Reel Editing</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Turn your aerial footage into a cinematic highlight reel showcasing your project milestones—complete with music, titles, and your company branding. Perfect for stakeholder updates or social media.</p>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <Button href="/contact?addons=true&bundle=true">Ask About Add-On Services</Button>
                </div>
              </div>
            )}

            {/* Upsell Options - Only for Real Estate category */}
            {category.id === 'realestate' && (
              <div className="mt-12 bg-brand-blue-900/20 border border-brand-blue-600/30 rounded-xl p-6 md:p-8">
                <h3 className="text-2xl font-bold text-center mb-6">Optional Add-On Services</h3>
                <p className="text-center text-gray-300 mb-4">Enhance any package with specialized options that help your listings stand out and sell faster</p>
                <p className="text-center text-brand-blue-600 font-medium mb-8 flex items-center justify-center">
                  <span className="text-xl mr-2">💡</span>
                  Bundle two or more add-ons and save 10%
                </p>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* 1️⃣ Property Boundary Overlay */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🗺️</span>
                      <h4 className="text-lg font-bold">Property Boundary Overlay</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Add clear visual context with an outline of the property boundaries displayed on aerial photos and video—perfect for larger lots or rural acreage listings.</p>
                  </div>

                  {/* 2️⃣ Vertical Social Media Clips */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🎞️</span>
                      <h4 className="text-lg font-bold">Vertical Social Media Clips</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Get short, optimized 15-second clips for Instagram Reels, TikTok, or Facebook Stories. These eye-catching edits help agents stand out on every platform.</p>
                  </div>

                  {/* 3️⃣ Voiceover Narration */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🔊</span>
                      <h4 className="text-lg font-bold">Voiceover Narration</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Bring your listing video to life with a professional voiceover describing key property features and location highlights.</p>
                  </div>

                  {/* 4️⃣ Interior Walk-Through */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🏠</span>
                      <h4 className="text-lg font-bold">Interior Walk-Through</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Complement your aerial footage with smooth, stabilized interior clips using a handheld gimbal—ideal for showcasing open layouts and design flow.</p>
                  </div>

                  {/* 5️⃣ Agent On-Camera Intro/Outro */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">🎤</span>
                      <h4 className="text-lg font-bold">Agent On-Camera Intro/Outro</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Feature the listing agent introducing or wrapping up the property video. Adds a personal touch that builds trust and brand recognition.</p>
                  </div>

                  {/* 6️⃣ Next-Day Rush Delivery */}
                  <div className="bg-brand-black/50 p-6 rounded-lg border border-gray-700 hover:border-brand-blue-600/50 transition-colors">
                    <div className="flex items-center mb-3">
                      <span className="text-2xl mr-2">⚡</span>
                      <h4 className="text-lg font-bold">Next-Day Rush Delivery</h4>
                    </div>
                    <p className="text-brand-blue-600 font-bold mb-3"></p>
                    <p className="text-gray-300 text-sm">Need your photos and video fast? Jump the line with next-day turnaround for time-sensitive listings and tight deadlines.</p>
                  </div>
                </div>

                <div className="mt-8 text-center">
                  <Button href="/contact?realestate_addons=true&bundle=true">Ask About Add-On Services</Button>
                </div>
              </div>
            )}
            
          </Container>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-16 bg-brand-black text-white">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Need a Custom Solution?</h2>
            <p className="text-lg mb-8">
              Our packages are designed to meet most needs, but we understand that every project is unique.
              Contact us to discuss your specific requirements and get a customized solution.
            </p>
            <Button href="/contact">Request Custom Package</Button>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePackagesPage;
